import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, ViewEncapsulation} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {DynFormData} from '../../domain/data/dyn-form-data';
import {DynSubformFieldData} from '../../domain/data/dyn-subform-field-data';
import {FormDataService} from '../../services/form-data.service';
import {ActivatedRoute, Router} from '@angular/router';
import {FormService} from '../../services/form.service';
import {Observable} from 'rxjs/Observable';
import {DynFieldData} from '../../domain/data/dyn-field-data';
import {GlobalAppService} from '../../../core/commons/global-app.service';
import {MessageSeverity} from '../../../core/commons/message-severity';

export enum Modes {
    view,
    preview,
    edit
}

@Component({
    selector: 'app-form-data',
    templateUrl: './form-data.component.html',
    styleUrls: ['./form-data.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class FormDataComponent implements OnInit {

    @Input() mode: Modes = Modes.edit;

    modes = Modes;

    formGroup: FormGroup;

    _formData: DynFormData;

    constructor(private fb: FormBuilder,
                private formService: FormService,
                private formDataService: FormDataService,
                private route: ActivatedRoute,
                private router: Router,
                private appService: GlobalAppService,
                private cd: ChangeDetectorRef) {
    }

    ngOnInit() {
        this.route.params
            .filter(params => params['dataId'] || params['formId'])
            .do(params => {
                this.appService.setLoading(true);
                if (params['mode']) {
                    switch (params['mode']) {
                        case 'preview':
                            this.mode = Modes.preview;
                            break;
                        case 'view':
                            this.mode = Modes.view;
                            break;
                        default:
                            this.mode = Modes.edit;
                    }
                }
            })
            .switchMap(params => {
                if (params['dataId']) {
                    return this.formDataService.get(+params['dataId'])
                } else if (params['formId']) {
                    return this.formService.get(+params['formId']).do(console.log)
                        .map(form => DynFormData.createEmpty(form))
                } else { return Observable.of(null); }
            })
            .subscribe(formData => {this.formData = formData; console.log('Form: '+JSON.stringify(formData))});
    }

    get formData(): DynFormData {
        return this._formData;
    }

    @Input()
    set formData(f: DynFormData) {
        if (f) {
            this._formData = f;
            this.appService.setLoading(true);
            this.formGroup = this.buildFormGroup(f);
            this.appService.setLoading(false);
            this.cd.markForCheck();
        }
    }

    public onSubmit({value, valid}) {
        if (valid && this.mode !== Modes.preview) {
            this.appService.setLoading(true);
            this.prepareFormData();
            this.formDataService.save(this.formData).subscribe(fo => {
                this.appService.setLoading(false);
                this.appService.sendMsg(
                    MessageSeverity.SUCCESS,
                    'Guardado',
                    'Se ha guardado el dictamen correctamente.');
                this.router.navigate(['home', 'forms', 'data'])
            }, error => {
                this.appService.setLoading(false);
                this.appService.sendMsg(
                    MessageSeverity.ERROR,
                    'Error',
                    'No ha sido posible guardar el dictamen. ' + error.json().message )
            })
        }
    }


    /**
     *
     * @param {DynFormData} formData
     * @returns {FormGroup}
     */
    private buildFormGroup(formData: DynFormData): FormGroup {
        const conf = {};
        formData.getValues().forEach(value => {
            const type = value.getField().getType();
            const name = value.getField().getName();
            if (type === 'subform') {
                conf[name] = this.buildFormGroup((value as DynSubformFieldData).getValue());
            } else {
                const validators = [];
                if (value.getField().getRequired()) {
                    validators.push(Validators.required);
                }
                if (value.getField().getMin() > 0) {
                    if (type === 'text' || type === 'textarea') {
                        validators.push(Validators.minLength(value.getField().getMin()))
                    } else {
                        validators.push(Validators.min(value.getField().getMin()));
                    }
                }
                if (value.getField().getMax() > 0) {
                    if (type === 'text' || type === 'textarea') {
                        validators.push(Validators.maxLength(value.getField().getMax()))
                    } else {
                        validators.push(Validators.max(value.getField().getMax()));
                    }
                }
                let valueData = null;
                if (type === 'date' && value.getValue()) {
                    valueData = value.getTypedValue() as Date;
                } else if (type === 'select' && value.getValue()) {
                    valueData = value.getValue().id;
                } else {
                    //ultima opcion, valor por defecto
                    if (value.getValue() == undefined) valueData = value.getField().getDefaultValue();
                    else valueData = value.getValue();
                }
                conf[name] = [valueData, Validators.compose(validators)];
            }
        });
        return this.fb.group(conf);
    }


    private prepareFormData() {
        this.copyFormGroupToFormData(this.formGroup, this.formData);
    }

    private copyFormGroupToFormData(formGroup: FormGroup, formData: DynFormData) {
        formData.setValues(formData.getValues().map((fieldData: DynFieldData) => {
            const formControl = formGroup.get(fieldData.getField().getName());
            switch (fieldData.getField().getType()) {
                case 'subform':
                    this.copyFormGroupToFormData(formControl as FormGroup, fieldData.getValue());
                    break;
                case 'date':
                    fieldData.setValue(formControl.value ? (formControl.value as Date).getTime() : null);
                    break;
                case 'fillableText':
                    fieldData.setType('fillableText');
                    fieldData.setValue(formControl.value);
                    break;
                default:
                    fieldData.setValue(formControl.value);
            }
            return fieldData;
        }));
    }
}




// WEBPACK FOOTER //
// ./src/app/forms/components/form-data/form-data.component.ts